import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

const cardTones = {
  sky: {
    surface: 'border-sky-200 bg-gradient-to-br from-white via-sky-50 to-blue-100/60',
    header: 'border-sky-200 bg-gradient-to-r from-sky-100/80 to-blue-50',
    footer: 'border-sky-200 bg-sky-100/60'
  },
  emerald: {
    surface: 'border-emerald-200 bg-gradient-to-br from-white via-emerald-50 to-teal-100/60',
    header: 'border-emerald-200 bg-gradient-to-r from-emerald-100/80 to-teal-50',
    footer: 'border-emerald-200 bg-emerald-100/60'
  },
  amber: {
    surface: 'border-amber-200 bg-gradient-to-br from-white via-amber-50 to-orange-100/60',
    header: 'border-amber-200 bg-gradient-to-r from-amber-100/80 to-orange-50',
    footer: 'border-amber-200 bg-amber-100/60'
  },
  violet: {
    surface: 'border-violet-200 bg-gradient-to-br from-white via-violet-50 to-indigo-100/60',
    header: 'border-violet-200 bg-gradient-to-r from-violet-100/80 to-indigo-50',
    footer: 'border-violet-200 bg-violet-100/60'
  }
};

const getFixedCardTone = () => {
  if (typeof window === 'undefined') return 'sky';

  const path = window.location.pathname.toLowerCase();

  if (path.includes('attendance') || path.includes('wfh')) return 'emerald';
  if (path.includes('leave') || path.includes('calendar')) return 'sky';
  if (path.includes('claim') || path.includes('report') || path.includes('performance')) return 'violet';
  if (
    path.includes('setting') ||
    path.includes('admin') ||
    path.includes('employee') ||
    path.includes('team') ||
    path.includes('announcement') ||
    path.includes('notification') ||
    path.includes('help')
  ) return 'amber';

  return 'sky';
};

const CardSurfaceContext = React.createContext({ hasCustomSurface: false, palette: cardTones.sky });

const Card = ({ 
  children, 
  className, 
  hover = false, 
  gradient = false, 
  glass = false,
  tone,
  ...props 
}) => {
  const resolvedTone = cardTones[tone] ? tone : getFixedCardTone();
  const palette = cardTones[resolvedTone];
  const hasCustomSurface = typeof className === 'string' && /(^|\s)(bg-|from-|via-|to-)/.test(className);
  const baseClasses = "rounded-xl shadow-sm border";
  const surfaceClasses = hasCustomSurface || gradient || glass
    ? "border-gray-200"
    : palette.surface;
  const hoverClasses = hover ? "card-hover" : "";
  const gradientClasses = gradient ? "bg-gradient-to-br from-white to-gray-50" : "";
  const glassClasses = glass ? "glass" : "";

  return (
    <CardSurfaceContext.Provider value={{ hasCustomSurface: hasCustomSurface || gradient || glass, palette }}>
      <motion.div
        className={clsx(
          baseClasses,
          surfaceClasses,
          hoverClasses,
          gradientClasses,
          glassClasses,
          className
        )}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        {...props}
      >
        {children}
      </motion.div>
    </CardSurfaceContext.Provider>
  );
};

const CardHeader = ({ children, className, ...props }) => {
  const { hasCustomSurface: parentHasCustomSurface, palette } = React.useContext(CardSurfaceContext);
  const hasCustomSurface = typeof className === 'string' && /(^|\s)(bg-|from-|via-|to-)/.test(className);
  const surfaceClasses = hasCustomSurface
    ? 'border-gray-200'
    : parentHasCustomSurface
      ? 'border-white/70 bg-white/60'
      : palette.header;

  return (
    <div
      className={clsx(
        "px-5 py-3 border-b",
        surfaceClasses,
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

const CardBody = ({ children, className, ...props }) => (
  <div className={clsx("px-5 py-3", className)} {...props}>
    {children}
  </div>
);

const CardFooter = ({ children, className, ...props }) => {
  const { hasCustomSurface, palette } = React.useContext(CardSurfaceContext);

  return (
    <div
      className={clsx(
        "px-5 py-3 border-t",
        hasCustomSurface ? 'border-white/70 bg-white/60' : palette.footer,
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;

export default Card;
